import { useQuery } from "@tanstack/react-query";

import type { TenantAccount } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient";
import { Authorizations } from "~/api/types";
import { useTeam } from "~/contexts/TeamProvider";
import { tryCatch } from "~/helpers/tryCatch";

type AccountListResp = Awaited<ReturnType<typeof apiClient.tenant.accountList>>;

export type AccountListData = AccountListResp["data"];

type AccountListDataPatched = AccountListData & {
  ["tenant/account"]?: TenantAccount;
  authorizations?: Authorizations;
};

export type TenantData = {
  account: TenantAccount | null;
  authorizations: Authorizations | undefined;
};

type TenantMode = "basic" | "withAuth";

export function useTenantAccount(
  mode: TenantMode,
  opts?: { enabled?: boolean },
) {
  const { team } = useTeam();

  return useQuery<TenantData>({
    queryKey: ["tenantAccount", team?.id, mode],
    enabled: (opts?.enabled ?? true) && Boolean(team?.id),
    // do not refetch
    staleTime: Infinity,
    queryFn: async (): Promise<TenantData> => {
      const request =
        mode === "withAuth"
          ? apiClient.tenant.accountList({
              headers: {
                "Authorize-Member-Actions": "update,show",
                "Authorize-Collection-Actions": "create",
              },
            })
          : apiClient.tenant.accountList();

      const { data: resp, error } = await tryCatch<
        { data: AccountListDataPatched },
        { status: number; error?: { authorizations?: Authorizations } }
      >(request);

      if (error) {
        if (error.status === 403)
          return { account: null, authorizations: undefined };
        if (error.status === 404)
          return { account: null, authorizations: error.error?.authorizations };
        throw error;
      }

      const data = resp.data as {
        ["tenant/account"]?: TenantAccount;
        authorizations?: Authorizations;
      };

      return {
        account: data["tenant/account"] ?? null,
        authorizations: mode === "withAuth" ? data.authorizations : undefined,
      };
    },
  });
}
